import { ChangeEvent, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { currentUser } from '@/reducers'
import {
  UPLOAD_UTILITY_BILL_SUCCESS,
  uploadUtilityBillForm,
} from '@/actions/plans'
import { RSAAResultAction } from 'redux-api-middleware'
import { User } from '@/types'
import {
  BillUploadExtraction,
  BillUploadStatus,
  MAX_FILE_SIZE_MB,
  readBillExtraction,
  readStoredBillUpload,
} from '@/app/features/utility-account-fields'

/** What the uploaded bill let us do, used to pick the message + CTAs on the page. */
export type BillUploadOutcome =
  /** A utility AND a plan were matched — fill both dropdowns. */
  | 'complete_match'
  /** No complete match, but the bill has a rate schedule — offer "Build my plan". */
  | 'buildable'
  /** Nothing usable was read — direct the user to add a utility/plan manually. */
  | 'manual'

export function deriveOutcome(
  extraction: BillUploadExtraction | undefined,
): BillUploadOutcome {
  const hasUtility = extraction?.matched_utility != null
  const hasPlan = extraction?.matched_plan != null
  if (hasUtility && hasPlan) {
    return 'complete_match'
  }
  if (extraction?.has_rate_schedule) {
    return 'buildable'
  }
  return 'manual'
}

type Options = {
  /** Forwarded to the extraction endpoint when present (enrollment flow). */
  utilityProgramId?: number | string
}

/**
 * Owns the "upload a utility bill" interaction for the utility/plan selection
 * page. Reuses the shared upload action and extraction readers, but — unlike the
 * account-fields hook — it surfaces the matched utility/plan, rate type, and rate
 * schedule (not account-number autofill) so the page can pre-fill the dropdowns,
 * offer to build a plan, or fall back to manual selection.
 *
 * Uploads with `reset_plan=true` so the backend attaches a fully-matched
 * utility/plan to the profile (and clears a stale plan when nothing matched).
 */
export function useBillUploadForSelection({ utilityProgramId }: Options = {}) {
  const dispatch = useAppDispatch()
  const user = useAppSelector(currentUser)
  // The extraction already stored on the profile (from an earlier upload or
  // step), independent of any fresh in-session upload.
  const persistedExtraction = readBillExtraction(user)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileUploadStatus, setFileUploadStatus] =
    useState<BillUploadStatus>('idle')
  const [fileUploadName, setFileUploadName] = useState<string | null>(null)
  const [uploadErrorMessage, setUploadErrorMessage] = useState<{
    title: string
    subtitle: string
  } | null>(null)
  const [billUrl, setBillUrl] = useState<string | null>(null)
  const [extraction, setExtraction] = useState<
    BillUploadExtraction | undefined
  >(undefined)
  const [outcome, setOutcome] = useState<BillUploadOutcome | null>(null)
  // Bumped on each successful upload so consumers can react to a fresh result.
  const [resultToken, setResultToken] = useState(0)

  const uploadUtilityBill = async (file: File) => {
    setFileUploadStatus('loading')
    setUploadErrorMessage(null)
    setFileUploadName(null)
    setBillUrl(null)
    setExtraction(undefined)
    setOutcome(null)

    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      setFileUploadStatus('error')
      setFileUploadName(file.name)
      setUploadErrorMessage({
        title: `${file.name} has an error.`,
        subtitle: `File size is too large, upload a file smaller than ${MAX_FILE_SIZE_MB}MB.`,
      })
      return
    }

    const data = new FormData()
    data.append('file', file)

    const uploadResponse: RSAAResultAction = await dispatch(
      uploadUtilityBillForm(data, true, true, utilityProgramId),
    )

    if (uploadResponse.type === UPLOAD_UTILITY_BILL_SUCCESS) {
      const updatedUser = (uploadResponse as { payload?: User }).payload
      const nextExtraction = readBillExtraction(updatedUser)
      setFileUploadName(file.name)
      setBillUrl(readStoredBillUpload(updatedUser)?.s3_url ?? null)
      setExtraction(nextExtraction)
      setOutcome(deriveOutcome(nextExtraction))
      setFileUploadStatus('success')
      setResultToken((token) => token + 1)
    } else {
      setFileUploadStatus('error')
      setFileUploadName(file.name)
      setUploadErrorMessage({
        title: 'An unexpected error occurred.',
        subtitle:
          'Please try again or select your utility and plan manually below.',
      })
    }
  }

  const handleFilesUploaded = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Clear the input so selecting the same file again still fires onChange.
    event.target.value = ''
    if (!file) {
      return
    }
    await uploadUtilityBill(file)
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  return {
    fileInputRef,
    handleFilesUploaded,
    handleUploadClick,
    fileUploadStatus,
    fileUploadName,
    uploadErrorMessage,
    billUrl,
    /** Full extraction from the most recent successful upload. */
    extraction,
    /** Derived outcome of the most recent successful upload. */
    outcome,
    /** Increments on each successful upload (use as an effect dependency). */
    resultToken,
    /**
     * True only when the profile already has a stored bill *extraction* from
     * before — a bare upload with no extraction does not count.
     */
    hasStoredExtraction: persistedExtraction != null,
    persistedExtraction,
    /** Presigned URL of the previously-stored bill, if any. */
    persistedBillUrl: readStoredBillUpload(user)?.s3_url ?? null,
  }
}
